import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Check,
  ChevronsUpDown,
  Loader2,
  Search,
  Shield,
  Scissors,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ActiveSalonBrand } from "@/components/active-salon-brand";
import { useSwitchSalon } from "@/hooks/use-switch-salon";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { trpc } from "@/trpc/trpc";
import { cn } from "@/lib/utils";

type SalonOption = {
  id: string;
  name: string;
  subtitle?: string | null;
  logoUrl?: string | null;
};

function normalize(s: string) {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/**
 * Header of the salon sidebar: the active salon's logo and name. When the
 * person can operate more than one salon, clicking it opens a popover with a
 * search box, a shortcut to the admin panel (admins only) and the list of
 * salons to switch to. Operators only ever see the static brand.
 */
export function SalonSwitcher({
  subtitle,
  isAdmin,
  isOperator,
  showAdminLink = true,
  salonPanelLink = false,
}: {
  subtitle: string;
  isAdmin: boolean;
  isOperator: boolean;
  /** Shortcut to /admin inside the popover (salon sidebar). */
  showAdminLink?: boolean;
  /** Shortcut to the active salon's panel (admin sidebar). */
  salonPanelLink?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const debouncedQuery = useDebouncedValue(query.trim(), 250);

  const { data: activeSalon } = trpc.salon.getOwnerSalon.useQuery();
  // Admins can open any active salon of their organisation; the search runs
  // on the server so organisations with more salons than one page still
  // reach every entry. Plain owners only see the salons they own.
  const { data: adminSalons, isFetching: adminFetching } =
    trpc.admin.listSalons.useQuery(
      { page: 1, limit: 100, search: debouncedQuery || undefined },
      { enabled: isAdmin, placeholderData: (prev) => prev },
    );
  const { data: ownedSalons } = trpc.salon.getOwnerSalons.useQuery(undefined, {
    enabled: !isOperator && !isAdmin,
  });
  const { switchTo, isPending, pendingSalonId } = useSwitchSalon();

  const options: SalonOption[] = isAdmin
    ? (adminSalons?.items ?? [])
        .filter((s) => s.status === "active")
        .map((s) => ({
          id: s.id,
          name: s.name,
          subtitle: s.code,
          logoUrl: s.logoUrl,
        }))
    : (ownedSalons ?? []).map((s) => ({
        id: s.id,
        name: s.name,
        subtitle: s.code,
        logoUrl: s.logoUrl,
      }));

  // Admins always get the switcher, whatever role their current profile has;
  // it is also their way into the admin panel.
  const canSwitch = isAdmin || (!isOperator && options.length > 1);

  if (!canSwitch) {
    return <ActiveSalonBrand subtitle={subtitle} />;
  }

  const needle = normalize(query.trim());
  const visible = isAdmin
    ? options
    : needle
      ? options.filter(
          (s) =>
            normalize(s.name).includes(needle) ||
            normalize(s.subtitle ?? "").includes(needle),
        )
      : options;
  const adminTruncated = isAdmin && (adminSalons?.total ?? 0) > 100;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-left hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          title="Przełącz salon"
          aria-label="Przełącz salon"
        >
          <div className="min-w-0 flex-1">
            <ActiveSalonBrand subtitle={subtitle} />
          </div>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" sideOffset={8} className="w-80 p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Szukaj salonu..."
            className="pl-8"
            autoComplete="off"
          />
        </div>

        {isAdmin && showAdminLink && (
          <Link
            to="/admin"
            onClick={() => setOpen(false)}
            className="mt-2 flex items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-primary hover:bg-accent"
          >
            <Shield className="size-4" />
            Przejdź do panelu admina
          </Link>
        )}
        {salonPanelLink && activeSalon && (
          <Link
            to="/"
            onClick={() => setOpen(false)}
            className="mt-2 flex items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-primary hover:bg-accent"
          >
            <Scissors className="size-4" />
            Otwórz panel: {activeSalon.name}
          </Link>
        )}

        <p className="mt-2 px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Salony
        </p>
        <div
          className={cn(
            "mt-1 max-h-72 overflow-y-auto",
            adminFetching && "opacity-70",
          )}
          role="listbox"
        >
          {visible.length === 0 ? (
            <p className="px-2 py-4 text-center text-sm text-muted-foreground">
              Brak salonów pasujących do wyszukiwania
            </p>
          ) : (
            visible.map((salon) => {
              const isCurrent = salon.id === activeSalon?.id;
              const isSwitching = pendingSalonId === salon.id;
              return (
                <button
                  key={salon.id}
                  type="button"
                  role="option"
                  aria-selected={isCurrent}
                  disabled={isCurrent || isPending}
                  onClick={() => {
                    setOpen(false);
                    switchTo(salon.id);
                  }}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm hover:bg-accent disabled:cursor-default disabled:hover:bg-transparent",
                    isCurrent && "bg-accent/50",
                  )}
                >
                  <Avatar className="size-8 shrink-0">
                    {salon.logoUrl && (
                      <AvatarImage src={salon.logoUrl} alt="" />
                    )}
                    <AvatarFallback className="bg-muted text-sm font-semibold text-muted-foreground">
                      {salon.name.trim().charAt(0).toUpperCase() || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{salon.name}</span>
                    {salon.subtitle && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {salon.subtitle}
                      </span>
                    )}
                  </span>
                  {isSwitching ? (
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  ) : isCurrent ? (
                    <Check className="size-4 text-primary" />
                  ) : (
                    <Scissors className="size-4 text-muted-foreground/50" />
                  )}
                </button>
              );
            })
          )}
        </div>
        {adminTruncated && !debouncedQuery && (
          <p className="px-2 pt-2 text-xs text-muted-foreground">
            Pokazano pierwsze 100 z {adminSalons?.total} salonów. Wpisz nazwę,
            aby znaleźć pozostałe.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
